/**
 * Exponential Backoff Utility
 *
 * Implements the standard exponential backoff formula with jitter:
 * t_wait = min(t_base · 2^n · (1 + jitter), t_max)
 *
 * Based on Auth0's recommended retry strategy for rate-limited requests.
 */

import { BACKOFF_BASE_MS, BACKOFF_MAX_MS, BACKOFF_JITTER } from '../sessionConfig';

/**
 * Calculate backoff delay with exponential growth and jitter
 *
 * @param {number} attempt - The current retry attempt (0-indexed)
 * @param {Object} options - Optional configuration overrides
 * @param {number} options.base - Base delay in ms (default: 100)
 * @param {number} options.max - Maximum delay in ms (default: 60000)
 * @param {number} options.jitter - Jitter factor 0-1 (default: 0.2 = ±20%)
 * @returns {number} Delay in milliseconds
 *
 * @example
 * // Attempt 0: ~100ms
 * // Attempt 1: ~200ms
 * // Attempt 2: ~400ms
 * // Attempt 3: ~800ms
 * // Attempt 4: ~1600ms
 * // ...capped at max (60000ms)
 */
export const calculateBackoff = (
  attempt,
  { base = BACKOFF_BASE_MS, max = BACKOFF_MAX_MS, jitter = BACKOFF_JITTER } = {}
) => {
  // Calculate exponential delay: base * 2^attempt
  const exponentialDelay = base * Math.pow(2, attempt);

  // Apply jitter: multiply by (1 ± jitter)
  // Random value between (1 - jitter) and (1 + jitter)
  const jitterMultiplier = 1 + (Math.random() * 2 - 1) * jitter;

  // Apply jitter and cap at max
  return Math.min(Math.round(exponentialDelay * jitterMultiplier), max);
};

/**
 * Create a backoff controller for managing retry state
 *
 * @param {Object} options - Configuration options
 * @param {number} options.maxRetries - Maximum number of retries
 * @param {number} options.base - Base delay in ms
 * @param {number} options.max - Maximum delay in ms
 * @param {number} options.jitter - Jitter factor
 * @returns {Object} Backoff controller
 *
 * @example
 * const backoff = createBackoffController({ maxRetries: 5 });
 *
 * while (backoff.canRetry()) {
 *   try {
 *     await makeRequest();
 *     backoff.reset();
 *     break;
 *   } catch (error) {
 *     const delay = backoff.nextDelay();
 *     await sleep(delay);
 *   }
 * }
 */
export const createBackoffController = ({
  maxRetries = 5,
  base = BACKOFF_BASE_MS,
  max = BACKOFF_MAX_MS,
  jitter = BACKOFF_JITTER,
} = {}) => {
  let attempt = 0;

  return {
    /**
     * Check if more retries are allowed
     */
    canRetry: () => attempt < maxRetries,

    /**
     * Get current attempt number
     */
    getAttempt: () => attempt,

    /**
     * Calculate and return the next delay, incrementing attempt counter
     */
    nextDelay: () => {
      const delay = calculateBackoff(attempt, { base, max, jitter });
      attempt++;
      return delay;
    },

    /**
     * Reset attempt counter (call after successful operation)
     */
    reset: () => {
      attempt = 0;
    },

    /**
     * Check if we've exceeded max retries
     */
    isExhausted: () => attempt >= maxRetries,
  };
};

/**
 * Sleep utility for use with backoff
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise<void>}
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
